import React from "react";
import { useQuery } from "wasp/client/operations";
import { getRetailStoreById, deleteRetailStore } from "wasp/client/operations";
import { useAction } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
} from "../../components/layout";

import {
  DollarSign,
  Tag,
  Phone,
  Package,
  Edit,
  AlertCircle,
  Ruler,
  Palette,
  Star,
  Building2,
  Clock,
  MapPin,
  Globe,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";

export default function ViewRetailPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const deleteRetailStoreFn = useAction(deleteRetailStore);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Fetch the store data
  const {
    data: store,
    isLoading,
    error,
  } = useQuery(getRetailStoreById, { storeId: itemId! });

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/retail`);
    } else {
      navigate("/retail");
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this store? This action cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await deleteRetailStoreFn({ storeId: itemId! });
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/retail`);
      } else {
        navigate("/retail");
      }
    } catch (err) {
      alert("Failed to delete store. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-content-text mb-2">
              Access Denied
            </h3>
            <p className="text-content-text-muted">
              You must be logged in to view retail stores.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-content-text-muted">Loading store...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !store) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-content-text mb-2">
              Store Not Found
            </h3>
            <p className="text-content-text-muted">
              The store you're looking for doesn't exist.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      CLOTHING: "bg-purple-100 text-purple-800",
      ELECTRONICS: "bg-blue-100 text-blue-800",
      HOME_GARDEN: "bg-green-100 text-green-800",
      SPORTS: "bg-orange-100 text-orange-800",
      BOOKS: "bg-yellow-100 text-yellow-800",
      BEAUTY: "bg-pink-100 text-pink-800",
      TOYS: "bg-red-100 text-red-800",
      AUTOMOTIVE: "bg-gray-100 text-gray-800",
      FOOD_BEVERAGE: "bg-amber-100 text-amber-800",
      HEALTHCARE: "bg-emerald-100 text-emerald-800",
      PROFESSIONAL_SERVICES: "bg-slate-100 text-slate-800",
      OTHER: "bg-indigo-100 text-indigo-800",
    };
    return (
      colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getStoreTypeColor = (storeType: string) => {
    const colors = {
      RETAIL_STORE: "bg-blue-100 text-blue-800",
      RESTAURANT: "bg-orange-100 text-orange-800",
      SERVICE_SHOP: "bg-green-100 text-green-800",
      BOUTIQUE: "bg-pink-100 text-pink-800",
      DEPARTMENT_STORE: "bg-purple-100 text-purple-800",
      SPECIALTY_STORE: "bg-yellow-100 text-yellow-800",
      OTHER: "bg-gray-100 text-gray-800",
    };
    return (
      colors[storeType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: [
        {
          label: "Buy and Sell",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/buy-sell`
            : "/buy-sell",
        },
        {
          label: "Food Court",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/food-court`
            : "/food-court",
        },
        {
          label: "Lost and Found",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/lost-found`
            : "/lost-found",
        },
        {
          label: "Retail",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/retail`
            : "/retail",
        },
        {
          label: "Services",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/services`
            : "/services",
        },
      ],
    },
    {
      label: "Retail",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/retail`
        : "/retail",
    },
  ];

  const isAdmin = user?.isAdmin;

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={
                selectedLocation?.locationCode
                  ? `/locations/${selectedLocation.locationCode}/retail/${store.id}/edit`
                  : `/retail/${store.id}/edit`
              }
              icon={Edit}
            >
              Edit Store
            </ActionButtonLink>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Store Type"
            value={store.metadata?.storeType?.replace("_", " ") || "N/A"}
          />
          <DetailField
            label="Category"
            value={store.metadata?.category || "N/A"}
          />
          <DetailField
            label="Posted by"
            value={store.author?.name || store.author?.email || "N/A"}
          />
          <DetailField
            label="Posted on"
            value={new Date(store.createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Store Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-content-text">
                {store.title}
              </h2>
              <SubscribeButton contentId={store.id} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                  store.metadata?.category,
                )}`}
              >
                {store.metadata?.category}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getStoreTypeColor(
                  store.metadata?.storeType,
                )}`}
              >
                {store.metadata?.storeType?.replace("_", " ")}
              </span>
              {store.metadata?.isVerified && (
                <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                  Verified
                </span>
              )}
              {store.metadata?.isFeatured && (
                <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                  Featured
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-content-text-muted text-lg leading-relaxed">
          {store.description}
        </p>
      </div>

      {/* Store Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-content-text mb-4">
          Store Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {store.metadata?.businessHours && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Business Hours
                  </div>
                  <div className="text-content-text">
                    {store.metadata.businessHours}
                  </div>
                </div>
              </div>
            )}

            {store.metadata?.unitNumber && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Unit Number
                  </div>
                  <div className="text-content-text">
                    {store.metadata.unitNumber}
                  </div>
                </div>
              </div>
            )}

            {store.metadata?.floor && (
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Floor
                  </div>
                  <div className="text-content-text">
                    {store.metadata.floor}
                  </div>
                </div>
              </div>
            )}

            {store.metadata?.building && (
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Building
                  </div>
                  <div className="text-content-text">
                    {store.metadata.building}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {store.metadata?.website && (
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Website
                  </div>
                  <a
                    href={store.metadata.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {store.metadata.website}
                  </a>
                </div>
              </div>
            )}

            {store.metadata?.contactInfo && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-content-text-muted mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-content-text-muted">
                    Contact Info
                  </div>
                  <div className="text-content-text">
                    {store.metadata.contactInfo}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-content-text-muted mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-content-text-muted rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-content-text-muted">
                  Store Owner
                </div>
                <div className="text-content-text">
                  {store.author.name || store.author.email}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-content-text-muted mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-content-text-muted rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-content-text-muted">
                  Listed
                </div>
                <div className="text-content-text">
                  {new Date(store.createdAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      {store.metadata?.services && store.metadata.services.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-content-text mb-4">
            Services Offered
          </h3>
          <div className="flex flex-wrap gap-2">
            {store.metadata.services.map((service: string, index: number) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
              >
                {service}
              </span>
            ))}
          </div>
        </div>
      )}
    </IndividualViewPageLayout>
  );
}
